import React, { useState } from "react";
import { Button, styled, Box, Typography } from "@mui/material";
import { useApolloClient } from "@apollo/client";
import { AUTHORIZE_GSC } from "../../gql/queries/gsc";

const GSCConnectionWrapper = styled("section")(({ theme }) => ({
  marginBottom: "20px",

  "& .connectionCard": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#ffffff",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "6px",
    padding: "24px",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    boxShadow: "none",
  },

  "& h2": {
    fontSize: "20px",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: "18px",
  },

  "& .statusBadge": {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: "14px",
  },

  "& .connectedBadge": {
    backgroundColor: theme.palette.mode === "dark" ? "#1e4620" : "#d4edda",
    color: theme.palette.mode === "dark" ? "#a3f7b5" : "#155724",
  },

  "& .disconnectedBadge": {
    backgroundColor: theme.palette.mode === "dark" ? "#4a1f24" : "#f8d7da",
    color: theme.palette.mode === "dark" ? "#ffb3b3" : "#721c24",
  },

  "& .statusDot": {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },

  "& .email": {
    fontSize: "14px",
    color: theme.palette.text.secondary,
    marginBottom: "14px",
  },

  "& .description": {
    fontSize: "14px",
    color: theme.palette.text.secondary,
    marginBottom: "18px",
    lineHeight: 1.6,
  },

  "& .primaryBtn": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 600,

    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },

    "&.Mui-disabled": {
      opacity: 0.4,
    },
  },

  "& .secondaryBtn": {
    backgroundColor: theme.palette.error.main,
    fontWeight: 600,
    color: "#fff",
    padding: "10px 20px",
    fontSize: "14px",

    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

function GSCConnection({ authStatus, onConnect, onDisconnect }) {
  const [connecting, setConnecting] = useState(false);
  const client = useApolloClient();

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await client.query({
        query: AUTHORIZE_GSC,
        fetchPolicy: "network-only",
      });

      const authUrl = data?.authorizeGsc?.authorization_url;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        throw new Error("Authorization URL not returned");
      }
    } catch (error) {
      console.error("Error initiating OAuth:", error);
      alert("Failed to initiate Google authentication");
      setConnecting(false);
    }
  };

  return (
    <GSCConnectionWrapper>
      <Box className="connectionCard">
        <Typography variant="h2">Google Search Console Connection</Typography>

        {authStatus?.authenticated ? (
          <Box>
            <Box className="statusBadge connectedBadge">
              <span className="statusDot" />
              Connected
            </Box>

            <Box className="email">{authStatus.email}</Box>

            <Button className="secondaryBtn" onClick={onDisconnect}>
              Disconnect
            </Button>
          </Box>
        ) : (
          <Box>
            <Box className="statusBadge disconnectedBadge">
              <span className="statusDot" />
              Not Connected
            </Box>

            <Box className="description">Connect your Google account to fetch keyword rankings from Google Search Console.</Box>

            <Button className="primaryBtn" onClick={handleConnect} disabled={connecting}>
              {connecting ? "Connecting..." : "Connect Google Account"}
            </Button>
          </Box>
        )}
      </Box>
    </GSCConnectionWrapper>
  );
}

export default GSCConnection;
